import { createContext, useContext } from 'react';

import {
  MatchableKind,
  SuggestableKind,
} from '@/modules/aiSuggestions/aiSuggestions.types';
import { CommonAiSuggestionFragment } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { AiSuggestionKind } from '@/types/schema';

export interface TestamentaryEntitySuggestionsContextType {
  /**
   * Suggestions grouped by kind
   */
  suggestionsByKind: Record<AiSuggestionKind, CommonAiSuggestionFragment[]>;
  /**
   * Get suggestions for the current section based on the section's suggestion kinds
   */
  getCurrentSectionSuggestions: (
    currentSection: EditTestamentaryEntitySection
  ) => CommonAiSuggestionFragment[];
  /**
   * Returns the suggestable suggestion of a specific kind (for single-field suggestions).
   */
  getSuggestableSuggestion: (
    kind: SuggestableKind
  ) => CommonAiSuggestionFragment | undefined;
  /**
   * Returns the suggestion of a specific kind that got matched with the given id.
   * Used to render the post-acceptance locator icon on subtrust form fields.
   */
  getMatchableSuggestion: (
    kind: MatchableKind,
    matchedId: string
  ) => CommonAiSuggestionFragment | undefined;
  /**
   * Refetch suggestions from the server
   */
  refetchSuggestions: (variables?: { testamentaryEntityID: string }) => void;
  /**
   * Whether the suggestions are currently loading
   */
  loading: boolean;
  /**
   * Document IDs associated with the suggestions (for evidence highlighting)
   */
  suggestionDocumentIds: string[];
}

const TestamentaryEntitySuggestionsContext =
  createContext<TestamentaryEntitySuggestionsContextType | null>(null);

export function useTestamentaryEntitySuggestionsContext(): TestamentaryEntitySuggestionsContextType {
  const context = useContext(TestamentaryEntitySuggestionsContext);
  if (!context) {
    throw new Error(
      'useTestamentaryEntitySuggestionsContext must be used within a TestamentaryEntitySuggestionsProvider'
    );
  }
  return context;
}

/**
 * Use this hook when you need to access the context but it may not be available
 * (e.g., in a component that's rendered outside the provider).
 */
export function useTestamentaryEntitySuggestionsContextIfAvailable(): TestamentaryEntitySuggestionsContextType | null {
  return useContext(TestamentaryEntitySuggestionsContext);
}

export { TestamentaryEntitySuggestionsContext };
